import React from 'react';
import { Building2, ArrowRight } from 'lucide-react';

// Expedia Group Affiliate Collective (EGAC) — Brian's short-link.
// Every click through this URL is tracked by Expedia and credits our
// affiliate account. Kept as a constant so it's trivial to swap if the
// link ever changes.
const EXPEDIA_AFFILIATE_URL = 'https://www.expedia.com/affiliates/expedia-home.OVp7sDn';

/**
 * Hero-style "Book a stay in Paisley" affiliate tile.
 *
 * Design intent: a single big CTA that reads like it belongs on the site
 * (Paisley Abbey backdrop, editorial copy, no "AD" screaming banner), so
 * click-through stays high while remaining honest — the small "Affiliate
 * link" tag under the button is required by the ASA CAP code for paid
 * partnerships.
 */
const BookAStayTile = () => {
  const trackClick = () => {
    // Fire a GA4 event so we can attribute revenue back to this tile in
    // the funnel dashboards. Wrapped in a try/catch so it can never
    // interfere with the actual outbound navigation.
    try {
      if (window.gtag) {
        window.gtag('event', 'affiliate_click', {
          affiliate: 'expedia',
          placement: 'homepage_book_a_stay_tile',
        });
      }
    } catch (_e) { /* no-op */ }
  };

  return (
    <section
      className="max-w-6xl mx-auto px-4 py-10"
      aria-label="Book a stay in Paisley — Expedia affiliate"
      data-testid="book-a-stay-tile"
    >
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-lg bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-800 text-white">
        {/* Ambient decorative blobs — pure CSS, no image weight. */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center p-8 md:p-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-300">
              <Building2 size={14} /> Book a stay
            </div>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-3">
              Visiting Paisley? Find a hotel from the town centre out to Glasgow Airport.
            </h2>
            <p className="text-slate-300 text-sm md:text-base max-w-xl">
              Hundreds of hotels, apartments and short-lets in and around Paisley — best-price guarantee, free cancellation on most stays.
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-2">
            <a
              href={EXPEDIA_AFFILIATE_URL}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={trackClick}
              data-testid="book-a-stay-cta"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-cyan-100 transition-colors rounded-full px-6 py-3 font-bold text-sm shadow-lg shadow-cyan-500/20 whitespace-nowrap"
            >
              Search hotels on Expedia
              <ArrowRight size={16} />
            </a>
            <span className="text-[10px] uppercase tracking-wider text-slate-400">
              Affiliate link · we may earn a small commission
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookAStayTile;
